import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BarChart3, TrendingUp, Users, Calendar, FileText, Mail } from 'lucide-react';
import { lazy, Suspense } from 'react';

const BarChart = lazy(() => import('recharts').then(module => ({ default: module.BarChart })));
const Bar = lazy(() => import('recharts').then(module => ({ default: module.Bar })));
const XAxis = lazy(() => import('recharts').then(module => ({ default: module.XAxis })));
const YAxis = lazy(() => import('recharts').then(module => ({ default: module.YAxis })));
const CartesianGrid = lazy(() => import('recharts').then(module => ({ default: module.CartesianGrid })));
const Tooltip = lazy(() => import('recharts').then(module => ({ default: module.Tooltip })));
const ResponsiveContainer = lazy(() => import('recharts').then(module => ({ default: module.ResponsiveContainer })));
const PieChart = lazy(() => import('recharts').then(module => ({ default: module.PieChart })));
const Pie = lazy(() => import('recharts').then(module => ({ default: module.Pie })));
const Cell = lazy(() => import('recharts').then(module => ({ default: module.Cell })));

interface AnalyticsData {
  total_submissions: number;
  today_submissions: number;
  week_submissions: number;
  month_submissions: number;
  form_type: string;
}

export default function AdminAnalytics() {
  const [analytics, setAnalytics] = useState<AnalyticsData[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const { data, error } = await supabase
        .from('admin_analytics_summary')
        .select('*');

      if (error) throw error;
      setAnalytics(data || []);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const { totalSubmissions, todaySubmissions, weekSubmissions, monthSubmissions } = useMemo(() => {
    return {
      totalSubmissions: analytics.reduce((sum, item) => sum + (item.total_submissions || 0), 0),
      todaySubmissions: analytics.reduce((sum, item) => sum + (item.today_submissions || 0), 0),
      weekSubmissions: analytics.reduce((sum, item) => sum + (item.week_submissions || 0), 0),
      monthSubmissions: analytics.reduce((sum, item) => sum + (item.month_submissions || 0), 0),
    };
  }, [analytics]);

  const chartData = useMemo(() => analytics.map(item => ({
    type: item.form_type,
    name: item.form_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown',
    total: item.total_submissions || 0,
    week: item.week_submissions || 0,
    month: item.month_submissions || 0
  })), [analytics]);

  const pieData = useMemo(() => analytics.map(item => ({
    type: item.form_type,
    name: item.form_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown',
    value: item.total_submissions || 0
  })), [analytics]);

  const COLORS = useMemo(() => ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'], []);

  const getFormRoute = useCallback((formType: string) => {
    const routes: Record<string, string> = {
      'early_adopter': '/admin/forms/early-adopters',
      'contact': '/admin/forms/contact',
      'professional_application': '/admin/forms/professionals',
      'sponsor_inquiry': '/admin/forms/sponsors',
      'story_submission': '/admin/forms/stories'
    };
    return routes[formType] || '/admin/forms';
  }, []);
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Analytics Dashboard</h1>
        <p className="text-muted-foreground">
          Track form submissions and engagement metrics
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Submissions</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalSubmissions}</div>
            <p className="text-xs text-muted-foreground">All time</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Today</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{todaySubmissions}</div>
            <p className="text-xs text-muted-foreground">New submissions today</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This Week</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{weekSubmissions}</div>
            <p className="text-xs text-muted-foreground">Past 7 days</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This Month</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{monthSubmissions}</div>
            <p className="text-xs text-muted-foreground">Past 30 days</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Grid */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Bar Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Submissions by Form Type</CardTitle>
            <CardDescription>Total submissions across different forms</CardDescription>
          </CardHeader>
          <CardContent>
            <Suspense fallback={<div className="h-[300px] w-full flex items-center justify-center">Loading Chart...</div>}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip />
                <Bar dataKey="total" fill="#8884d8">
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`bar-total-${index}`}
                      cursor="pointer"
                      onClick={useCallback(() => navigate(getFormRoute(entry.type)), [navigate, getFormRoute, entry.type])}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            </Suspense>
          </CardContent>
        </Card>

        {/* Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Form Distribution</CardTitle>
            <CardDescription>Percentage breakdown of form submissions</CardDescription>
          </CardHeader>
          <CardContent>
            <Suspense fallback={<div className="h-[300px] w-full flex items-center justify-center">Loading Chart...</div>}>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                      cursor="pointer"
                      onClick={useCallback(() => navigate(getFormRoute(entry.type)), [navigate, getFormRoute, entry.type])}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            </Suspense>
          </CardContent>
        </Card>

        {/* Weekly Trend */}
        <Card>
          <CardHeader>
            <CardTitle>Weekly Trends</CardTitle>
            <CardDescription>Weekly submission trends by form type</CardDescription>
          </CardHeader>
          <CardContent>
            <Suspense fallback={<div className="h-[300px] w-full flex items-center justify-center">Loading Chart...</div>}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip />
                <Bar dataKey="week" fill="#82ca9d">
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`bar-week-${index}`}
                      cursor="pointer"
                      onClick={useCallback(() => navigate(getFormRoute(entry.type)), [navigate, getFormRoute, entry.type])}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            </Suspense>
          </CardContent>
        </Card>

        {/* Monthly Trend */}
        <Card>
          <CardHeader>
            <CardTitle>Monthly Trends</CardTitle>
            <CardDescription>Monthly submission trends by form type</CardDescription>
          </CardHeader>
          <CardContent>
            <Suspense fallback={<div className="h-[300px] w-full flex items-center justify-center">Loading Chart...</div>}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip />
                <Bar dataKey="month" fill="#ffc658">
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`bar-month-${index}`}
                      cursor="pointer"
                      onClick={useCallback(() => navigate(getFormRoute(entry.type)), [navigate, getFormRoute, entry.type])}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            </Suspense>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Table */}
      <Card>
        <CardHeader>
          <CardTitle>Detailed Breakdown</CardTitle>
          <CardDescription>Complete analytics breakdown by form type</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Form Type</th>
                  <th className="text-right p-2">Total</th>
                  <th className="text-right p-2">Today</th>
                  <th className="text-right p-2">This Week</th>
                  <th className="text-right p-2">This Month</th>
                </tr>
              </thead>
              <tbody>
                {analytics.map((item, index) => (
                  <tr
                    key={index}
                    className="border-b hover:bg-muted/50 cursor-pointer"
                    onClick={useCallback(() => navigate(getFormRoute(item.form_type)), [navigate, getFormRoute, item.form_type])}
                  >
                    <td className="p-2 font-medium">
                      {item.form_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown'}
                    </td>
                    <td className="text-right p-2">{item.total_submissions || 0}</td>
                    <td className="text-right p-2">{item.today_submissions || 0}</td>
                    <td className="text-right p-2">{item.week_submissions || 0}</td>
                    <td className="text-right p-2">{item.month_submissions || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}